import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { OutreachPlanChoice } from "@/hooks/useRequestOutreach";
import { FONT_SIZE, INK, OUTREACH_POPUP, RADIUS, SHADOW } from "@/theme/tokens";
import ValueChoiceGrid from "@/ui/Clickable/ValueChoiceGrid";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { OUTREACH_LEVELS, type OutreachAllowed, type OutreachLevel, type RequestOutreach } from "@/utils/models";
import { LEVEL_OF_ALLOWED } from "@/utils/outreachRules";
import { levelIndexOf, levelNumberOf } from "./outreachGroupTypes";
import type { OutreachCopy } from "./useOutreachCopy";

type Props = {
	outreach: RequestOutreach;
	copy: OutreachCopy;
	onApply: (choice: OutreachPlanChoice) => void;
	onCancel: () => void;
};

const ALLOWED_KEYS: readonly (keyof OutreachAllowed)[] = ["calls", "overtime", "agency"];

/** Above our pick: keep stepping up when this level runs out, or stay on it. */
enum AfterChoice {
	StepUp = "step_up",
	Hold = "hold",
}

const isAllowedKey = (value: number | string): value is keyof OutreachAllowed => ALLOWED_KEYS.some((key) => key === value);
const isLevel = (value: number | string): value is OutreachLevel => OUTREACH_LEVELS.some((level) => level === value);

const Field: FC<{ labelId: string; label: string; children: ReactNode }> = ({ labelId, label, children }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
		<Typography id={labelId} sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.SECONDARY }}>
			{label}
		</Typography>
		{children}
	</Box>
);

const sameAllowed = (a: OutreachAllowed, b: OutreachAllowed): boolean => ALLOWED_KEYS.every((key) => a[key] === b[key]);

/**
 * Change the plan for one shift: what outreach may use and how hard it pushes —
 * with, when pushing above our pick, whether it keeps stepping up after. Drafts until applied.
 */
export const OutreachAdjustPanel: FC<Props> = ({ outreach, copy, onApply, onCancel }) => {
	const { t } = useTranslation();
	const ids = { allowed: useId(), level: useId(), after: useId() };
	const { effort } = outreach;
	const [allowed, setAllowed] = useState(effort.allowed);
	const [level, setLevel] = useState(effort.level);
	const [holding, setHolding] = useState(effort.holding);
	const ourPick = levelIndexOf(effort.automaticLevel);
	const picked = levelIndexOf(level);
	const lastLevel = OUTREACH_LEVELS.length - 1;
	const showAfter = picked > ourPick && picked < lastLevel;
	// Stepping below our pick always holds; above it, holding is the user's call; at our pick there's nothing to hold.
	const holdingNow = picked < ourPick || (showAfter && holding);
	const changed = !sameAllowed(allowed, effort.allowed) || level !== effort.level || holdingNow !== effort.holding;
	const afterItems = [
		{
			value: AfterChoice.StepUp,
			label: t("request.outreach.adjust.after.step_up"),
			caption: t("request.outreach.adjust.after.step_up_caption"),
			dataTestId: "outreach-adjust-after-step-up",
		},
		{
			value: AfterChoice.Hold,
			label: t("request.outreach.adjust.after.hold", { number: levelNumberOf(level) }),
			caption: t("request.outreach.adjust.after.hold_caption"),
			dataTestId: "outreach-adjust-after-hold",
		},
	].map((item) => ({ ...item, ariaLabel: `${item.label}, ${item.caption}` }));

	return (
		<Box
			role="region"
			aria-label={t("request.outreach.adjust.title")}
			data-testid="outreach-adjust-panel"
			sx={{
				px: 2,
				py: 1.75,
				borderRadius: RADIUS.CONTROL / RADIUS.CARD,
				border: (theme) => `1px solid ${alpha(theme.palette.primary.main, OUTREACH_POPUP.ADJUST_BORDER_ALPHA)}`,
				boxShadow: SHADOW.PANEL_SOFT_HOVER,
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
			}}
		>
			<Box>
				<Typography component="h3" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: INK.PRIMARY }}>
					{t("request.outreach.adjust.title")}
				</Typography>
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
					{t("request.outreach.adjust.subtitle", { level: copy.levelNumber(effort.automaticLevel) })}
				</Typography>
			</Box>
			<Field labelId={ids.allowed} label={t("request.outreach.adjust.allowed_label")}>
				<ValueChoiceGrid
					tileVariant="outline"
					density="compact"
					columns={{ xs: 1, sm: 3 }}
					ariaLabelledBy={ids.allowed}
					dataTestId="outreach-adjust-allowed"
					multiSelected={ALLOWED_KEYS.filter((key) => allowed[key])}
					onSelect={(value) => isAllowedKey(value) && setAllowed((current) => ({ ...current, [value]: !current[value] }))}
					items={ALLOWED_KEYS.map((key) => {
						const label = t(`request.outreach.adjust.allowed.${key}`);
						const caption = t(`request.outreach.adjust.allowed.${key}_caption`, { number: levelNumberOf(LEVEL_OF_ALLOWED[key]) });
						return { value: key, label, caption, ariaLabel: `${label}, ${caption}`, dataTestId: `outreach-adjust-allowed-${key}` };
					})}
				/>
			</Field>
			<Field labelId={ids.level} label={t("request.outreach.adjust.level_label")}>
				<ValueChoiceGrid
					tileVariant="outline"
					density="compact"
					columns={{ xs: 1, sm: 5 }}
					ariaLabelledBy={ids.level}
					dataTestId="outreach-adjust-level"
					selected={level}
					onSelect={(value) => isLevel(value) && setLevel(value)}
					items={OUTREACH_LEVELS.map((option, index) => {
						const tag = index === ourPick ? t("request.outreach.adjust.tag.our_pick") : undefined;
						return {
							value: option,
							tag,
							label: copy.levelNumber(option),
							caption: copy.levelName(option),
							// The tile's figure is only "Level 3"; announce what the level does too.
							ariaLabel: [copy.levelOption(option), ...(tag === undefined ? [] : [tag])].join(", "),
							dataTestId: `outreach-adjust-level-${option}`,
						};
					})}
				/>
			</Field>
			{showAfter && (
				<Field labelId={ids.after} label={t("request.outreach.adjust.after_label")}>
					<ValueChoiceGrid
						tileVariant="outline"
						density="compact"
						columns={{ xs: 1, sm: 2 }}
						ariaLabelledBy={ids.after}
						dataTestId="outreach-adjust-after"
						selected={holding ? AfterChoice.Hold : AfterChoice.StepUp}
						onSelect={(value) => setHolding(value === AfterChoice.Hold)}
						items={afterItems}
					/>
				</Field>
			)}
			{picked < ourPick && (
				<Typography role="status" sx={{ fontSize: FONT_SIZE.CAPTION, lineHeight: 1.45, color: INK.SECONDARY }}>
					{t("request.outreach.adjust.lower_note")}
				</Typography>
			)}
			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
				<SystemButton data-testid="outreach-adjust-cancel" buttonVariant="borderless" size="compact" noFullWidthMobile onClick={onCancel}>
					{t("cancel")}
				</SystemButton>
				<SystemButton
					data-testid="outreach-adjust-apply"
					buttonVariant="primary"
					size="compact"
					noFullWidthMobile
					disabled={!changed}
					onClick={() => onApply({ level, holding: holdingNow, allowed })}
				>
					{t("apply")}
				</SystemButton>
			</Box>
		</Box>
	);
};
